// ============================================================================
// ADD AVATAR FROM CATALOG MODAL
// Browse organization-wide catalog avatars and add one into the running session
// ============================================================================

import React, { useEffect, useState } from 'react';
import { X, Loader2, Library, AlertCircle } from 'lucide-react';
import { AvatarDefinition, AvatarScope } from '../types';
import { browseAvatars, instantiateAvatarInSession } from '../services/avatarCatalogService';

export interface AddAvatarFromCatalogModalProps {
  sessionId: string;
  existingAvatarDefinitionIds: string[];
  onClose: () => void;
  onAdded: (avatar: AvatarDefinition) => void;
}

export const AddAvatarFromCatalogModal: React.FC<AddAvatarFromCatalogModalProps> = ({
  sessionId,
  existingAvatarDefinitionIds,
  onClose,
  onAdded,
}) => {
  const [loading, setLoading] = useState(true);
  const [catalogAvatars, setCatalogAvatars] = useState<AvatarDefinition[]>([]);
  const [error, setError] = useState('');
  const [addingId, setAddingId] = useState<string | null>(null);

  // Fetch once on mount. `existingAvatarDefinitionIds` is a fresh array on every parent
  // render (recomputed via .map/.filter), so it must NOT be a dependency here — the
  // "already in session" filter is applied below at render time instead.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const result = await browseAvatars({ scope: AvatarScope.ORGANIZATION });

        // The backend's browse endpoint only filters by scope when an ownerId is also
        // given, and its underlying queries don't dedupe old versions (createNewVersion
        // never deprecates the previous one) — so filter and dedupe defensively here,
        // keeping only the latest version of each avatarDefinitionId.
        const latestByDefinitionId = new Map<string, AvatarDefinition>();
        for (const a of result.avatars) {
          if (a.scope !== AvatarScope.ORGANIZATION) continue;
          const existing = latestByDefinitionId.get(a.avatarDefinitionId);
          if (!existing || a.versionNumber > existing.versionNumber) {
            latestByDefinitionId.set(a.avatarDefinitionId, a);
          }
        }

        if (!cancelled) {
          setCatalogAvatars(Array.from(latestByDefinitionId.values()));
        }
      } catch {
        if (!cancelled) setError('Failed to load the avatar catalog — please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const avatars = catalogAvatars.filter(a => !existingAvatarDefinitionIds.includes(a.avatarDefinitionId));

  const handleAdd = async (avatar: AvatarDefinition) => {
    setAddingId(avatar.avatarDefinitionId);
    setError('');
    try {
      await instantiateAvatarInSession(sessionId, avatar.avatarDefinitionId);
      onAdded(avatar);
      onClose();
    } catch {
      setError(`Failed to add ${avatar.name} — please try again.`);
      setAddingId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-5"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] flex flex-col shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Library size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-bold text-navy">Add from Catalog</h2>
              <p className="text-xs text-slate-500">Organization-wide avatars available to any session</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="px-6 py-3 border-b border-slate-200 bg-red-50 flex gap-2 items-start flex-shrink-0">
            <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center items-center py-16 text-slate-400">
              <Loader2 size={24} className="animate-spin" />
            </div>
          ) : avatars.length === 0 ? (
            <div className="text-center py-16 px-4">
              <p className="text-sm text-slate-500">No organization avatars available to add.</p>
              <p className="text-xs text-slate-400 mt-1">Either none exist yet, or they're all already in this session.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {avatars.map(avatar => {
                const isAdding = addingId === avatar.avatarDefinitionId;
                return (
                  <button
                    key={avatar.avatarDefinitionId}
                    type="button"
                    disabled={addingId !== null}
                    onClick={() => handleAdd(avatar)}
                    className="flex items-center justify-between p-3 rounded-xl border border-slate-200 text-left transition-all hover:border-primary/40 hover:bg-slate-50 disabled:opacity-60"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-sm font-bold overflow-hidden"
                        style={{ background: avatar.primaryColor || '#00c7c4' }}
                      >
                        {avatar.imageUrl ? (
                          <img src={avatar.imageUrl} alt={avatar.name} className="w-full h-full object-cover" />
                        ) : (
                          avatar.name.charAt(0).toUpperCase()
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-navy truncate">{avatar.name}</p>
                        <p className="text-xs text-slate-400 truncate">{avatar.description}</p>
                      </div>
                    </div>
                    <div className="flex-shrink-0 pl-2">
                      {isAdding ? <Loader2 size={16} className="animate-spin text-primary" /> : null}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddAvatarFromCatalogModal;
